import { Header } from "@/components/layout/Header";
import { HeroSection } from "@/components/sections/HeroSection";
import { ComplianceCard } from "@/components/compliance/ComplianceCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, FileText, GitBranch } from "lucide-react";

const Index = () => {
  const sampleNetworks = [
    {
      title: "Production Line A - Building 3",
      description: "47 PLCs • 12 HMIs • 3 switches • Last updated by IT",
      status: "compliant" as const,
      lastUpdated: "2024-11-20",
    },
    {
      title: "SCADA Network - Main Plant",
      description: "23 devices • Someone added a laptop? (needs review)",
      status: "warning" as const,
      lastUpdated: "2024-11-20",
    },
    {
      title: "Building Automation - HQ",
      description: "89 devices • 7 firewall rules pending approval",
      status: "non-compliant" as const,
      lastUpdated: "2024-11-19",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <HeroSection />

      {/* Beta Tester Callout - Playful IT/OT Angle */}
      <section className="py-12 bg-accent/5 border-y border-accent/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">OT Engineers & IT Teams: We Need Your Input</h2>
            <p className="text-lg text-muted-foreground mb-6">
              We're building ComplyManager <strong>with</strong> you, not <strong>for</strong> you. If you've ever spent
              3 hours on a firewall rule request or lost a network diagram in someone's email, you know the pain we're
              solving.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-card rounded-lg border border-accent/20">
                <h4 className="font-semibold mb-2">✓ Free Beta Access</h4>
                <p className="text-sm text-muted-foreground">
                  No credit card. No commitment. Just test it and tell us what sucks.
                </p>
              </div>
              <div className="p-4 bg-card rounded-lg border border-accent/20">
                <h4 className="font-semibold mb-2">✓ Shape the Product</h4>
                <p className="text-sm text-muted-foreground">
                  Direct line to the dev team. Your feedback = our roadmap.
                </p>
              </div>
              <div className="p-4 bg-card rounded-lg border border-accent/20">
                <h4 className="font-semibold mb-2">✓ Early Adopter Perks</h4>
                <p className="text-sm text-muted-foreground">Locked-in pricing when we launch. Plus bragging rights.</p>
              </div>
            </div>

            <p className="text-lg font-semibold text-primary">
              Interested?{" "}
              <a href="/auth" className="underline hover:text-accent transition-colors">
                Join the beta program →
              </a>
            </p>
            <p className="text-sm text-muted-foreground mt-4">Rolling admissions • We'll onboard you within a week</p>
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section id="how-it-works" className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">One place where everyone "SaaS" the same network.</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              No more "What is this system connected to?" or "Who supports or is responsible for this system?"
              Everything in one place, always up to date.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {sampleNetworks.map((item, index) => (
              <ComplianceCard key={index} {...item} />
            ))}
          </div>

          <div className="text-center">
            <Button variant="hero" size="lg">
              Explore Network View
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built to End the IT vs. OT Battle</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Made by people who understand both worlds. Because system documentation and asset relationships shouldn't
              be a source of conflict.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-card rounded-lg shadow-card">
              <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">System Documentation That Actually Works</h3>
              <p className="text-muted-foreground">
                Asset relationships, system layouts, and connections in one place. Version controlled, always current,
                and actually useful.
              </p>
            </div>
            <div className="text-center p-6 bg-card rounded-lg shadow-card">
              <GitBranch className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Track System Changes Without the Email Hell</h3>
              <p className="text-muted-foreground">
                Submit system updates, track modifications, manage ownership transfers – without pinging IT every 3
                hours.
              </p>
            </div>
            <div className="text-center p-6 bg-card rounded-lg shadow-card">
              <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Who Owns What? Now You Know.</h3>
              <p className="text-muted-foreground">
                See which systems connect where, who's responsible for what, and why. No more mystery configurations or
                lost ownership.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">Schedule a demo to see ComplyManager in action.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="hero"
              size="lg"
              className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
            >
              Schedule a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-background">
        <div className="container mx-auto px-4 text-center">
          <p>
            &copy; 2025{" "}
            <a
              href="https://www.autelion.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline font-medium"
            >
              Autelion AB
            </a>
            . All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
